import { useQuery } from 'react-query';
import axios from 'axios';
import Icon from '@mdi/react';
import { mdiLoading, mdiRefresh, mdiTextBoxOutline } from '@mdi/js';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import type { ConnectionLogEntryDTO } from '@webip/shared/src/dto/connection_status';

type ConnectionLogPanelProps = {
    applianceId: number;
    /** Tópicos do /log do MikroTik (ex.: ["ppp","l2tp"]). */
    topics: string[];
    title?: string;
    /** Intervalo de atualização automática em ms (0 = desativado). */
    refetchInterval?: number;
};

/**
 * Painel de logs de conexão (estilo terminal) lido do /log do MikroTik via
 * agente. Reutilizado nas telas de VPN e IPSec.
 */
const ConnectionLogPanel = ({
    applianceId,
    topics,
    title = 'Logs de conexão',
    refetchInterval = 20_000,
}: ConnectionLogPanelProps) => {
    const topicsParam = topics.join(',');
    const query = useQuery({
        queryKey: ['connection-logs', applianceId, topicsParam],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; agentOnline: boolean; result: ConnectionLogEntryDTO[] }>(
                `/api/appliance/${applianceId}/connection-logs?topics=${encodeURIComponent(topicsParam)}`,
                { withCredentials: true },
            );
            return res.data;
        },
        enabled: !!applianceId,
        refetchInterval: refetchInterval || false,
    });

    const data = query.data;
    const logs = data?.result ?? [];

    return (
        <VBox style={{ gap: '8px' }}>
            <HBox style={{ alignItems: 'center', gap: '8px' }}>
                <Icon path={mdiTextBoxOutline} size="18px" color="var(--color-muted)" />
                <span style={{ fontSize: '13px', fontWeight: 600 }}>{title}</span>
                {data && !data.agentOnline && (
                    <span style={{ fontSize: '12px', color: 'var(--status-offline)' }}>(agente desconectado)</span>
                )}
                <span style={{ flex: 1 }} />
                <Botao
                    size="sm"
                    variant="none-primary"
                    icon={query.isFetching ? mdiLoading : mdiRefresh}
                    iconProps={query.isFetching ? { spin: true } : undefined}
                    onClick={() => query.refetch()}
                    title="Atualizar logs"
                />
            </HBox>
            <pre
                style={{
                    background: 'var(--tc-color-gray-900, #111827)',
                    color: 'var(--tc-color-gray-100, #f3f4f6)',
                    borderRadius: '8px',
                    padding: '12px 14px',
                    fontFamily: 'monospace',
                    fontSize: '12.5px',
                    lineHeight: '1.6',
                    margin: 0,
                    maxHeight: '320px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {query.isLoading
                    ? 'Carregando...'
                    : logs.length
                        ? logs.map((l, i) => `${l.time}  [${l.topics}]  ${l.message}`).join('\n')
                        : '(sem registros)'}
            </pre>
        </VBox>
    );
};

export default ConnectionLogPanel;
